/**
 * Cross-page handle to the active Campaign Studio job.
 *
 * The progress bar (`CampaignStudioProgressBar`) lives on the AppShell
 * and renders only when this localStorage key is set. The Flow 1 form
 * calls `trackCampaignStudioJob(job_id)` on submit success; the bar
 * picks it up via the matching `cs-job-changed` window event.
 *
 * Pulled out of CampaignStudioProgressBar.tsx so the component file
 * exports only components (Vite's react-refresh rule).
 */

export const CS_ACTIVE_JOB_KEY = "oneads.cs.active_job_id";
export const CS_TERMINAL_TTL_MS = 5 * 60 * 1000;

interface ActiveJobEntry {
  id: number;
  terminal_at?: number | null;
}

export function readActiveJobId(): number | null {
  try {
    const raw = localStorage.getItem(CS_ACTIVE_JOB_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ActiveJobEntry;
    if (!parsed?.id) return null;
    if (
      parsed.terminal_at &&
      Date.now() - parsed.terminal_at > CS_TERMINAL_TTL_MS
    ) {
      localStorage.removeItem(CS_ACTIVE_JOB_KEY);
      return null;
    }
    return parsed.id;
  } catch {
    return null;
  }
}

export function setActiveJobId(id: number | null, terminal: boolean): void {
  if (id === null) {
    localStorage.removeItem(CS_ACTIVE_JOB_KEY);
    return;
  }
  localStorage.setItem(
    CS_ACTIVE_JOB_KEY,
    JSON.stringify({
      id,
      terminal_at: terminal ? Date.now() : null,
    }),
  );
}

/** Called from form mutation onSuccess — saves the id and pings the
 *  bar to re-render via a window event. */
export function trackCampaignStudioJob(jobId: number): void {
  setActiveJobId(jobId, false);
  window.dispatchEvent(new Event("cs-job-changed"));
}
